import { Trash2, Truck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, confirmDialog, DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDateTime, formatNumber } from '../../lib/intl.js';
import { formatMoney, trimMoney } from '../../lib/money.js';
import { movementRemovable } from '../items/ItemStockTab.js';

interface MovementRow {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  movementType: 'receipt' | 'shipment' | 'adjustment' | 'transfer_in' | 'transfer_out';
  quantity: string;
  unitCost: string | null;
  unitCostCurrencyCode: string | null;
  referenceType: string | null;
  occurredAt: string;
  note: string | null;
}

const TYPE_VARIANT: Record<
  MovementRow['movementType'],
  'success' | 'warning' | 'info' | 'secondary'
> = {
  receipt: 'success',
  shipment: 'warning',
  adjustment: 'info',
  transfer_in: 'success',
  transfer_out: 'warning',
};

export function StockMovementsPage(): JSX.Element {
  const { t, i18n } = useTranslation('inventory');
  const canAdjust = usePermission('stock.adjust');
  const [rows, setRows] = useState<MovementRow[] | null>(null);

  const reload = useCallback((): void => {
    void api()
      .get<{ items: MovementRow[] }>('/stock/movements')
      .then((r) => setRows(r.data.items));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // Delete a manual adjustment: the server reverses its quantity and takes its
  // value back out of the average, then drops the row. Destructive — confirm.
  const onRemove = async (m: MovementRow): Promise<void> => {
    const confirmed = await confirmDialog({
      title: t('movements.remove.title'),
      message: t('movements.remove.confirm', { quantity: trimMoney(m.quantity), warehouse: m.warehouseName }),
      tone: 'danger',
      confirmLabel: t('movements.remove.action'),
    });
    if (!confirmed) return;
    try {
      await api().delete(`/stock/movements/${m.id}`);
      toast.success(t('movements.remove.done'));
      reload();
    } catch (err) {
      const data = (err as { response?: { data?: { code?: string; cause?: { code?: string } } } }).response?.data;
      const code = data?.cause?.code ?? data?.code;
      if (code === 'stock.insufficient') toast.error(t('movements.remove.errors.insufficient'));
      else if (code === 'movement.not_removable') toast.error(t('movements.remove.errors.notRemovable'));
      else toast.error(t('movements.remove.errors.failed'));
    }
  };

  if (!rows) return <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>;
  if (rows.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <header>
          <h1 className="text-2xl font-semibold">{t('movements.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('movements.description')}</p>
        </header>
        <EmptyState
          icon={<Truck className="h-8 w-8" aria-hidden />}
          title={t('movements.empty')}
          description={t('movements.emptyDescription')}
        />
      </div>
    );
  }

  const columns: DataTableColumn<MovementRow>[] = [
    {
      id: 'occurredAt',
      header: t('movements.columns.occurredAt'),
      sortValue: (r) => r.occurredAt,
      cellClassName: 'text-fg-muted',
      cell: (r) => formatDateTime(r.occurredAt, { locale: i18n.language }),
    },
    {
      id: 'type',
      header: t('movements.columns.type'),
      sortValue: (r) => t(`movements.type.${r.movementType}`),
      searchValue: (r) => t(`movements.type.${r.movementType}`),
      cell: (r) => (
        <Badge variant={TYPE_VARIANT[r.movementType]}>
          {t(`movements.type.${r.movementType}`)}
        </Badge>
      ),
    },
    {
      id: 'warehouse',
      header: t('movements.columns.warehouse'),
      sortValue: (r) => r.warehouseCode,
      searchValue: (r) => `${r.warehouseCode} ${r.warehouseName}`,
      cell: (r) => (
        <>
          <span className="font-mono text-xs">{r.warehouseCode}</span> · {r.warehouseName}
        </>
      ),
    },
    {
      id: 'item',
      header: t('movements.columns.item'),
      sortValue: (r) => r.itemCode,
      searchValue: (r) => `${r.itemCode} ${r.itemName}`,
      cell: (r) => (
        <>
          <span className="font-mono text-xs">{r.itemCode}</span> · {r.itemName}
        </>
      ),
    },
    {
      id: 'quantity',
      header: t('movements.columns.quantity'),
      align: 'end',
      sortValue: (r) => Number(r.quantity),
      cellClassName: 'font-mono tabular-nums',
      // The raw column value is a 4-decimal DECIMAL, so every whole quantity
      // read "100.0000" — four zeros of noise on every row, and a 30071 that
      // took a moment to size. Decimals show only when the count actually has
      // them (a half metre, a 2.5 kg bag); thousands are grouped, because these
      // rows now run to five figures.
      cell: (r) => {
        const n = Number(r.quantity);
        return Number.isFinite(n)
          ? formatNumber(n, { locale: i18n.language }, { maximumFractionDigits: 4 })
          : r.quantity;
      },
    },
    {
      id: 'unitCost',
      header: t('movements.columns.unitCost'),
      align: 'end',
      sortValue: (r) => (r.unitCost && r.unitCostCurrencyCode ? Number(r.unitCost) : null),
      cellClassName: 'font-mono tabular-nums',
      cell: (r) =>
        r.unitCost && r.unitCostCurrencyCode
          ? formatMoney(r.unitCost, {
              locale: i18n.language,
              currency: r.unitCostCurrencyCode,
            })
          : '—',
    },
    {
      id: 'note',
      header: t('movements.columns.note'),
      sortValue: (r) => r.note ?? '',
      searchValue: (r) => r.note ?? '',
      cellClassName: 'text-fg-muted',
      cell: (r) => r.note ?? '—',
    },
  ];
  if (canAdjust) {
    columns.push({
      id: 'actions',
      header: '',
      align: 'end',
      cell: (r) =>
        movementRemovable(r) ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void onRemove(r)}
            aria-label={t('movements.remove.action')}
            title={t('movements.remove.action')}
          >
            <Trash2 className="h-4 w-4 text-danger" aria-hidden />
          </Button>
        ) : null,
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('movements.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('movements.description')}</p>
      </header>
      <Card className="overflow-hidden p-0">
        <DataTable columns={columns} data={rows} rowKey={(r) => r.id} />
      </Card>
    </div>
  );
}
